import type {
  SalesforceField,
  CsvColumn,
  LeadRow,
  ProblemRow,
  BonzoCampaign,
  Five9List,
  VendorRouting,
  UploadHistoryItem,
  FileMeta,
} from './types'

export const SALESFORCE_FIELDS: SalesforceField[] = [
  {
    id: 'firstName',
    label: 'First Name',
    required: true,
    aliases: ['first name', 'firstname', 'fname', 'first'],
  },
  {
    id: 'lastName',
    label: 'Last Name',
    required: true,
    aliases: ['last name', 'lastname', 'lname', 'last', 'surname'],
  },
  {
    id: 'email',
    label: 'Email',
    required: true,
    aliases: ['email', 'email address', 'e-mail'],
  },
  {
    id: 'phone',
    label: 'Phone',
    required: true,
    aliases: ['phone', 'phone number', 'mobile', 'cell', 'telephone'],
  },
  {
    id: 'vendor',
    label: 'Vendor',
    required: false,
    aliases: ['vendor', 'source vendor', 'data vendor'],
  },
  {
    id: 'leadSource',
    label: 'Lead Source',
    required: false,
    aliases: ['lead source', 'source', 'leadsource'],
  },
  {
    id: 'propertyState',
    label: 'Property State',
    required: false,
    aliases: ['property state', 'state', 'st'],
  },
  {
    id: 'loanPurpose',
    label: 'Loan Purpose',
    required: false,
    aliases: ['loan purpose', 'purpose', 'loan type'],
  },
  {
    id: 'estimatedHomeValue',
    label: 'Estimated Home Value',
    required: false,
    aliases: [
      'estimated home value',
      'home value',
      'property value',
      'est value',
    ],
  },
  {
    id: 'campaign',
    label: 'Campaign',
    required: false,
    aliases: ['campaign', 'campaign name', 'utm campaign'],
  },
]

export const DEFAULT_FILE_META: FileMeta = {
  name: 'mortgage_leads_july_2026.csv',
  sizeLabel: '1.42 MB',
  rows: 1284,
  columns: 10,
  uploadedAt: 'Jul 16, 2026 · 9:42 AM',
  validation: 'warning',
}

export const CSV_COLUMNS: CsvColumn[] = [
  {
    header: 'First Name',
    sample: 'Marcus',
    mappedTo: 'firstName',
    status: 'mapped',
  },
  {
    header: 'Last Name',
    sample: 'Delgado',
    mappedTo: 'lastName',
    status: 'mapped',
  },
  {
    header: 'Email Address',
    sample: 'marcus.delgado@gmail.com',
    mappedTo: 'email',
    status: 'mapped',
  },
  {
    header: 'Phone Number',
    sample: '(602) 555-0148',
    mappedTo: 'phone',
    status: 'mapped',
  },
  {
    header: 'Vendor',
    sample: 'LendingTree',
    mappedTo: 'vendor',
    status: 'mapped',
  },
  {
    header: 'Lead Source',
    sample: 'Paid Search',
    mappedTo: 'leadSource',
    status: 'mapped',
  },
  {
    header: 'Property State',
    sample: 'AZ',
    mappedTo: 'propertyState',
    status: 'mapped',
  },
  {
    header: 'Loan Purpose',
    sample: 'Refinance',
    mappedTo: 'loanPurpose',
    status: 'mapped',
  },
  {
    header: 'Est Home Value',
    sample: '$425,000',
    mappedTo: 'estimatedHomeValue',
    status: 'mapped',
  },
  {
    header: 'Campaign',
    sample: 'Q3 Refi Push',
    mappedTo: 'campaign',
    status: 'mapped',
  },
  {
    header: 'Notes',
    sample: 'Called twice, no answer',
    mappedTo: null,
    status: 'unsupported',
  },
]

export const PREVIEW_ROWS: LeadRow[] = [
  {
    row: 1,
    firstName: 'Marcus',
    lastName: 'Delgado',
    email: 'marcus.delgado@gmail.com',
    phone: '(602) 555-0148',
    vendor: 'LendingTree',
    leadSource: 'Paid Search',
    propertyState: 'AZ',
    loanPurpose: 'Refinance',
    estimatedHomeValue: '$425,000',
    campaign: 'Q3 Refi Push',
    validation: 'valid',
  },
  {
    row: 2,
    firstName: 'Priya',
    lastName: 'Nair',
    email: 'priya.nair@outlook.com',
    phone: '(408) 555-0192',
    vendor: 'Zillow',
    leadSource: 'Organic',
    propertyState: 'CA',
    loanPurpose: 'Purchase',
    estimatedHomeValue: '$980,000',
    campaign: 'First-Time Buyer',
    validation: 'valid',
  },
  {
    row: 3,
    firstName: 'DeShawn',
    lastName: 'Carter',
    email: 'deshawn.carter@',
    phone: '773-555-0110',
    vendor: 'LendingTree',
    leadSource: 'Paid Search',
    propertyState: 'IL',
    loanPurpose: 'Refinance',
    estimatedHomeValue: '$310,000',
    campaign: 'Q3 Refi Push',
    validation: 'error',
  },
  {
    row: 4,
    firstName: 'Emily',
    lastName: 'Sorensen',
    email: 'emily.sorensen@yahoo.com',
    phone: '555 0173',
    vendor: 'Bankrate',
    leadSource: 'Comparison',
    propertyState: 'MN',
    loanPurpose: 'Purchase',
    estimatedHomeValue: '250-300k',
    campaign: 'Summer Purchase',
    validation: 'warning',
  },
  {
    row: 5,
    firstName: 'Hiroshi',
    lastName: 'Tanaka',
    email: 'h.tanaka@gmail.com',
    phone: '(212) 555-0166',
    vendor: 'Zillow',
    leadSource: 'Organic',
    propertyState: 'NY',
    loanPurpose: 'Cash-Out',
    estimatedHomeValue: '$1,150,000',
    campaign: 'First-Time Buyer',
    validation: 'valid',
  },
  {
    row: 6,
    firstName: 'Alicia',
    lastName: '',
    email: 'alicia@gmail.com',
    phone: '(305) 555-0129',
    vendor: 'Bankrate',
    leadSource: 'Comparison',
    propertyState: 'FL',
    loanPurpose: 'Purchase',
    estimatedHomeValue: '$540,000',
    campaign: 'Summer Purchase',
    validation: 'warning',
  },
  {
    row: 7,
    firstName: 'Robert',
    lastName: 'Nguyen',
    email: 'robert.nguyen@gmail.com',
    phone: '(206) 555-0155',
    vendor: 'LendingTree',
    leadSource: 'Paid Search',
    propertyState: 'WA',
    loanPurpose: 'Refinance',
    estimatedHomeValue: '$675,000',
    campaign: 'Q3 Refi Push',
    validation: 'valid',
  },
]

export const PROBLEM_ROWS: ProblemRow[] = [
  {
    row: 3,
    name: 'DeShawn Carter',
    phone: '773-555-0110',
    email: 'deshawn.carter@',
    vendor: 'LendingTree',
    issue: 'Invalid email format',
    severity: 'error',
    suggestion: 'Add a valid domain (e.g. @gmail.com)',
  },
  {
    row: 4,
    name: 'Emily Sorensen',
    phone: '555 0173',
    email: 'emily.sorensen@yahoo.com',
    vendor: 'Bankrate',
    issue: 'Phone number too short',
    severity: 'error',
    suggestion: 'Expand to 10-digit number',
  },
  {
    row: 4,
    name: 'Emily Sorensen',
    phone: '555 0173',
    email: 'emily.sorensen@yahoo.com',
    vendor: 'Bankrate',
    issue: 'Home value is a range (250-300k)',
    severity: 'warning',
    suggestion: 'Normalize to $275,000',
  },
  {
    row: 6,
    name: 'Alicia —',
    phone: '(305) 555-0129',
    email: 'alicia@gmail.com',
    vendor: 'Bankrate',
    issue: 'Missing last name',
    severity: 'warning',
    suggestion: 'Split from full name if available',
  },
  {
    row: 12,
    name: 'Marcus Delgado',
    phone: '(602) 555-0148',
    email: 'marcus.delgado@gmail.com',
    vendor: 'LendingTree',
    issue: 'Duplicate of row 1',
    severity: 'warning',
    suggestion: 'Remove duplicate row',
  },
  {
    row: 18,
    name: 'Karen Whitfield',
    phone: '',
    email: 'kwhitfield@gmail.com',
    vendor: 'Zillow',
    issue: 'Missing phone number',
    severity: 'error',
    suggestion: 'Add a valid phone number',
  },
  {
    row: 24,
    name: 'Tom Becker',
    phone: '(512) 555-0177',
    email: 'tbecker@gmail.com',
    vendor: 'Bankrate',
    issue: 'State abbreviation "Texas"',
    severity: 'warning',
    suggestion: 'Standardize to "TX"',
  },
]

export const BONZO_CAMPAIGNS: BonzoCampaign[] = [
  {
    id: 'BZ-1042',
    name: 'Refi Nurture — Q3',
    description: 'Long-term refinance nurture sequence',
  },
  {
    id: 'BZ-1088',
    name: 'First-Time Buyer Welcome',
    description: 'Educational drip for new buyers',
  },
  {
    id: 'BZ-1120',
    name: 'Cash-Out Fast Follow',
    description: 'High-intent cash-out outreach',
  },
  {
    id: 'BZ-1155',
    name: 'Summer Purchase Blitz',
    description: 'Seasonal purchase campaign',
  },
  {
    id: 'BZ-1201',
    name: 'Comparison Shopper Re-Engage',
    description: 'Re-engagement for rate shoppers',
  },
]

export const FIVE9_LISTS: Five9List[] = [
  { id: 'L-3001', name: 'LendingTree — Warm Refi' },
  { id: 'L-3002', name: 'Zillow — Purchase Intent' },
  { id: 'L-3003', name: 'Bankrate — Comparison Leads' },
  { id: 'L-3004', name: 'General Inbound Queue' },
  { id: 'L-3005', name: 'High Value ($750k+)' },
]

/** Vendor → recommended Bonzo campaign + Five9 list (existing vendor-based mapping logic) */
export const VENDOR_ROUTING: VendorRouting[] = [
  {
    vendor: 'LendingTree',
    rowCount: 612,
    bonzoCampaignId: 'BZ-1042',
    five9ListId: 'L-3001',
    autoSelected: true,
  },
  {
    vendor: 'Zillow',
    rowCount: 428,
    bonzoCampaignId: 'BZ-1088',
    five9ListId: 'L-3002',
    autoSelected: true,
  },
  {
    vendor: 'Bankrate',
    rowCount: 244,
    bonzoCampaignId: 'BZ-1155',
    five9ListId: 'L-3003',
    autoSelected: true,
  },
]

export const UPLOAD_HISTORY: UploadHistoryItem[] = [
  {
    id: 'U-9021',
    date: 'Jul 15, 2026 · 4:12 PM',
    fileName: 'zillow_purchase_batch.csv',
    uploadedBy: 'Dana Alvarez',
    vendor: 'Zillow',
    totalRows: 806,
    salesforceResult: 'completed',
    bonzoCampaign: 'First-Time Buyer Welcome',
    bonzoResult: 'completed',
    five9List: 'Zillow — Purchase Intent',
    five9Result: 'completed',
    status: 'completed',
  },
  {
    id: 'U-9018',
    date: 'Jul 14, 2026 · 11:03 AM',
    fileName: 'lendingtree_refi.csv',
    uploadedBy: 'Marcus Lee',
    vendor: 'LendingTree',
    totalRows: 1240,
    salesforceResult: 'completed',
    bonzoCampaign: 'Refi Nurture — Q3',
    bonzoResult: 'completed-with-warnings',
    five9List: 'LendingTree — Warm Refi',
    five9Result: 'completed',
    status: 'completed-with-warnings',
  },
  {
    id: 'U-9014',
    date: 'Jul 12, 2026 · 2:47 PM',
    fileName: 'bankrate_mixed.csv',
    uploadedBy: 'Dana Alvarez',
    vendor: 'Bankrate',
    totalRows: 532,
    salesforceResult: 'completed',
    bonzoCampaign: 'Summer Purchase Blitz',
    bonzoResult: 'completed',
    five9List: 'Bankrate — Comparison Leads',
    five9Result: 'failed',
    status: 'partially-completed',
  },
  {
    id: 'U-9009',
    date: 'Jul 10, 2026 · 9:20 AM',
    fileName: 'multi_vendor_july.csv',
    uploadedBy: 'Priya Shah',
    vendor: 'Multiple',
    totalRows: 2104,
    salesforceResult: 'completed',
    bonzoCampaign: 'Multiple',
    bonzoResult: 'completed',
    five9List: 'Multiple',
    five9Result: 'completed-with-warnings',
    status: 'completed-with-warnings',
  },
  {
    id: 'U-9002',
    date: 'Jul 08, 2026 · 3:55 PM',
    fileName: 'test_upload.csv',
    uploadedBy: 'Marcus Lee',
    vendor: 'LendingTree',
    totalRows: 48,
    salesforceResult: 'failed',
    bonzoCampaign: '—',
    bonzoResult: 'failed',
    five9List: '—',
    five9Result: 'failed',
    status: 'failed',
  },
]
